import * as React from "react";
import { useState, useContext, useCallback } from "react";
import styled from "styled-components";
import { PluginStore } from "graylog-web-plugin/plugin";
import ExportModal from "views/components/export/ExportModal";
import MoveWidgetToTab from "views/logic/views/MoveWidgetToTab";
import { loadAsDashboard, loadDashboard } from "views/logic/views/Actions";
import { IconButton } from "components/common";
import View from "views/logic/views/View";
import Search from "views/logic/search/Search";
import CopyWidgetToDashboard from "views/logic/views/CopyWidgetToDashboard";
import IfSearch from "views/components/search/IfSearch";
import { MenuItem, DeleteMenuItem } from "components/bootstrap";
import iterateConfirmationHooks from "views/hooks/IterateConfirmationHooks";
import DrilldownContext from "views/components/contexts/DrilldownContext";
import useView from "views/hooks/useView";
import createSearch from "views/logic/slices/createSearch";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { selectQuery, updateView } from "views/logic/slices/viewSlice";
import { duplicateWidget, removeWidget } from "views/logic/slices/widgetActions";
import fetchSearch from "views/logic/views/fetchSearch";
import useHistory from "routing/useHistory";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useParameters from "views/hooks/useParameters";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import ExtractWidgetIntoNewView from "views/logic/views/ExtractWidgetIntoNewView";
import ExtraMenuWidgetActions from "views/components/widgets/ExtraMenuWidgetActions";
import { widgetActionsMenuClass } from "views/components/widgets/Constants";
import { getView, updateView as _updateView } from "views/api/views";
import ReplaySearchButton from "./ReplaySearchButton";
import ExtraDropdownWidgetActions from "./ExtraDropdownWidgetActions";
import CopyToDashboard from "./CopyToDashboardForm";
import MoveWidgetToTabModal from "./MoveWidgetToTabModal";
import WidgetActionDropdown from "./WidgetActionDropdown";
import WidgetHorizontalStretch from "./WidgetHorizontalStretch";
import IfInteractive from "../dashboard/IfInteractive";
import IfDashboard from "../dashboard/IfDashboard";
import WidgetFocusContext from "../contexts/WidgetFocusContext";
import WidgetContext from "../contexts/WidgetContext";
const Container = styled.div`
  position: relative; // required for absolute positioned widget actions
  display: flex;
  align-items: center;
  min-height: 100%;
`;
const WidgetActions = styled.div`
  display: flex;
  line-height: 0;
  height: 100%;
  align-items: center;

  > *:not(:last-child) {
    margin-right: 2px;
  }
`;
const _onCopyToDashboard = async (view, setShowCopyToDashboard, widgetId, dashboardId, history) => {
  if (!dashboardId) {
    return;
  }
  const dashboardJson = await getView(dashboardId);
  const dashboard = View.fromJSON(dashboardJson);
  const search = await fetchSearch(dashboardJson.search_id).then((searchJson) => Search.fromJSON(searchJson));
  const newDashboard = CopyWidgetToDashboard(widgetId, view, dashboard.toBuilder().search(search).build());
  if (newDashboard == null ? void 0 : newDashboard.search) {
    const newSearch = await createSearch(newDashboard.search);
    const newDashboardWithSearch = newDashboard.toBuilder().search(newSearch).build();
    await _updateView(newDashboardWithSearch);
    loadDashboard(history, newDashboardWithSearch.id);
  }
  setShowCopyToDashboard(false);
};
const _onCreateNewDashboard = async (view, widgetId, history) => {
  const newView = ExtractWidgetIntoNewView(view, widgetId);
  loadAsDashboard(history, newView);
};
const _onMoveWidgetToPage = async (dispatch, view, setShowMoveWidgetToTab, widgetId, queryId, keepCopy) => {
  if (!queryId) {
    return;
  }
  const newDashboard = MoveWidgetToTab(widgetId, queryId, view, keepCopy);
  if (newDashboard) {
    const searchResponse = await createSearch(newDashboard.search);
    const updatedDashboard = newDashboard.toBuilder().search(searchResponse).build();
    await dispatch(updateView(updatedDashboard, true));
    setShowMoveWidgetToTab(false);
    await dispatch(selectQuery(queryId));
  }
};
const defaultOnDeleteWidget = async (_widget, _view, title) => (
  // eslint-disable-next-line no-alert
  window.confirm(`Are you sure you want to remove the widget "${title}"?`)
);
const _onDelete = (widget, view, title) => async (dispatch) => {
  const pluggableWidgetDeletionHooks = PluginStore.exports("views.hooks.confirmDeletingWidget");
  const result = await iterateConfirmationHooks(
    [...pluggableWidgetDeletionHooks, defaultOnDeleteWidget],
    widget,
    view,
    title
  );
  return result === true ? dispatch(removeWidget(widget.id)) : Promise.resolve();
};
const _onDuplicate = (widgetId, unsetWidgetFocusing, title) => (dispatch) => dispatch(duplicateWidget(widgetId, title)).then(() => unsetWidgetFocusing());
const WidgetActionsMenu = ({ isFocused, onPositionsChange, position, title, toggleEdit }) => {
  const widget = useContext(WidgetContext);
  const view = useView();
  const { query, timerange, streams, streamCategories } = useContext(DrilldownContext);
  const { setWidgetFocusing, unsetWidgetFocusing } = useContext(WidgetFocusContext);
  const [showCopyToDashboard, setShowCopyToDashboard] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showMoveWidgetToTab, setShowMoveWidgetToTab] = useState(false);
  const dispatch = useViewsDispatch();
  const history = useHistory();
  const sendTelemetry = useSendTelemetry("search-widget");
  const { parameters, parameterBindings } = useParameters();
  const [overflowingComponents, setOverflowingComponents] = useState({});
  const overflowingComponentsValues = Object.values(overflowingComponents);
  const onDuplicate = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_WIDGET_ACTION.DUPLICATE, {
      app_action_value: "widget-duplicate-button"
    });
    return dispatch(_onDuplicate(widget.id, unsetWidgetFocusing, title));
  }, [sendTelemetry, dispatch, widget.id, unsetWidgetFocusing, title]);
  const onCopyToDashboard = useCallback(
    (widgetId, dashboardId) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_WIDGET_ACTION.COPY_TO_DASHBOARD, {
        app_action_value: "widget-copy-to-dashboard-button"
      });
      return _onCopyToDashboard(view, setShowCopyToDashboard, widgetId, dashboardId, history);
    },
    [history, sendTelemetry, view]
  );
  const onCreateNewDashboard = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_WIDGET_ACTION.CREATE_NEW_DASHBOARD, {
      app_action_value: "widget-create-new-dashboard-button"
    });
    return _onCreateNewDashboard(view, widget.id, history);
  }, [sendTelemetry, view, widget.id, history]);
  const onMoveWidgetToTab = useCallback(
    (widgetId, queryId, keepCopy) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_WIDGET_ACTION.MOVE, {
        app_action_value: "widget-move-button"
      });
      return _onMoveWidgetToPage(dispatch, view, setShowMoveWidgetToTab, widgetId, queryId, keepCopy);
    },
    [dispatch, sendTelemetry, view]
  );
  const onDelete = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_WIDGET_ACTION.DELETED, {
      app_action_value: "widget-delete-button"
    });
    return dispatch(_onDelete(widget, view, title));
  }, [dispatch, sendTelemetry, title, view, widget]);
  const focusWidget = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_WIDGET_ACTION.FOCUSED, {
      app_action_value: "widget-focus-button"
    });
    return setWidgetFocusing(widget.id);
  }, [sendTelemetry, setWidgetFocusing, widget.id]);
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(WidgetActions, { className: widgetActionsMenuClass }, /* @__PURE__ */ React.createElement(IfInteractive, null, /* @__PURE__ */ React.createElement(IfDashboard, null, /* @__PURE__ */ React.createElement(
    ReplaySearchButton,
    {
      queryString: query.query_string,
      timerange,
      streams,
      streamCategories,
      parameterBindings,
      parameters,
      filters: widget.filters,
      newTab: true
    }
  )), isFocused && /* @__PURE__ */ React.createElement(IconButton, { name: "fullscreen_exit", title: "Un-focus widget", onClick: unsetWidgetFocusing })), /* @__PURE__ */ React.createElement(ExtraMenuWidgetActions, { widget }), /* @__PURE__ */ React.createElement(IfInteractive, null, !isFocused && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    WidgetHorizontalStretch,
    {
      widgetId: widget.id,
      widgetType: widget.type,
      onStretch: onPositionsChange,
      position
    }
  ), /* @__PURE__ */ React.createElement(IconButton, { name: "fullscreen", title: "Focus this widget", onClick: focusWidget })), /* @__PURE__ */ React.createElement(IconButton, { name: "edit_square", title: "Edit", iconType: "regular", onClick: toggleEdit }), /* @__PURE__ */ React.createElement(WidgetActionDropdown, null, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: onDuplicate }, "Duplicate"), /* @__PURE__ */ React.createElement(IfSearch, null, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: () => setShowCopyToDashboard(true) }, "Copy to Dashboard")), /* @__PURE__ */ React.createElement(IfDashboard, null, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: () => setShowMoveWidgetToTab(true) }, "Move to Page")), /* @__PURE__ */ React.createElement(ExtraDropdownWidgetActions, { widget, setComponents: setOverflowingComponents }), /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: onDelete })), showCopyToDashboard && /* @__PURE__ */ React.createElement(
    CopyToDashboard,
    {
      onCopyToDashboard: (dashboardId) => onCopyToDashboard(widget.id, dashboardId),
      onCancel: () => setShowCopyToDashboard(false),
      submitLoadingText: "Copying widget...",
      submitButtonText: "Copy widget",
      onCreateNewDashboard
    }
  ), showExport && /* @__PURE__ */ React.createElement(ExportModal, { view, directExportWidgetId: widget.id, closeModal: () => setShowExport(false) }), showMoveWidgetToTab && /* @__PURE__ */ React.createElement(
    MoveWidgetToTabModal,
    {
      view,
      widgetId: widget.id,
      onCancel: () => setShowMoveWidgetToTab(false),
      onSubmit: onMoveWidgetToTab
    }
  )), overflowingComponentsValues));
};
export default WidgetActionsMenu;
